import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Inject, Optional } from '@angular/core';
import { ILogger, LogTypes, LogMessage } from './models/ILogger';
import { ILoggerConfiguration } from './models/ILoggerConfiguration';
import { LogLevel } from './models/LogLevel';
import { CapturedPayload } from './models/CapturedPayload';
import { ITransporter } from './models/ITransporter';
import { ConsoleTransporter } from './transporters/implementations/ConsoleTransporter';
import { LOGGER_CONFIGURATION_TOKEN, TRANSPORTER_TOKEN } from './InjectionTokens';

/**
 * Default Logger implementation
 */
@Injectable()
export class LoggerService<T = LogMessage> implements ILogger {
    private transporters?: Array<ITransporter<T>>;

    constructor(
        @Inject(LOGGER_CONFIGURATION_TOKEN) private loggerConfig: ILoggerConfiguration,
        @Optional() @Inject(TRANSPORTER_TOKEN) transporters?: Array<ITransporter<T>>
    ) {
        /*
         * Ensure array is received, if no received it means only one
         * transporter has been defined and was not configured as multi
         */
        this.transporters = (Array.isArray(transporters) ? transporters : [transporters]).filter(Boolean);
        if (!transporters.length) {
            // in case no transporters were defined we provide the console transporter by default
            this.transporters = [new ConsoleTransporter()];
        }
    }

    /**
     * Logs a info message
     * @param message
     */
    info(message: string | T): void {
        this.logWith(LogLevel.Info, message);
    }

    /**
     * Logs debug action messages
     * @param message
     */
    debug(message: string | T): void {
        this.logWith(LogLevel.Debug, message);
    }

    /**
     * Logs warning level messages
     * @param message
     */
    warn(message: string | T): void {
        this.logWith(LogLevel.Warn, message);
    }

    /**
     * Logs error messages
     * @param error
     */
    error(error: string | Error | HttpErrorResponse | T): void {
        this.logWith(LogLevel.Error, error);
    }

    /**
     * If payload evaluates as should be logged then a macro-task is scheduled
     * @param logLevel
     * @param payload
     */
    private logWith(logLevel: LogLevel, payload: LogTypes) {
        if (this.isEventLevelLessThanLogLevel(logLevel, payload)) {
            // capture payload, timestamp and level
            const capturedPayload: CapturedPayload = { payload, level: logLevel, timestamp: new Date() };
            // send captured payload to each transporter to be written
            for (const transport of this.transporters) {
                this.execute(transport, capturedPayload, logLevel);
            }
        }
    }

    /**
     * A log-able payload is it which it's log level is less or equal to configured log level
     * @param logLevel
     * @param payload
     */
    private isEventLevelLessThanLogLevel(logLevel: LogLevel, payload: LogTypes): boolean {
        return payload != null && this.loggerConfig.level >= logLevel;
    }

    /**
     * Queues the logging event as a micro-task
     * @param transport
     * @param payload
     * @param level
     */
    private execute(transport: ITransporter<T>, capturedPayload: CapturedPayload, level: LogLevel) {
        // send to event queue to minimize the stack processing for the application
        queueMicrotask(() => transport.write(capturedPayload, level));
    }
}
